import { useMemo, useState } from 'react';
import { Link, Navigate, NavLink } from 'react-router-dom';
import Card from '@/components/ui/Card';
import DonateShareCodeInput from '@/components/sponsors/DonateShareCodeInput';
import { useAuth } from '@/lib/auth-context';
import { sponsorsEnabled } from '@/lib/feature-flags';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
const SLOT_VALUE_CENTS = 80_000;

interface ResolvedClassroom {
  id: string;
  public_name?: string | null;
  is_confirmed?: boolean | null;
}

const howItWorks = [
  {
    title: 'Secure your slot',
    copy:
      'Paying $800 mints a Sponsor Slot and locks in your profile so our team can steward the match end-to-end.',
  },
  {
    title: 'Teacher confirmation',
    copy:
      'Classrooms opt in by confirming readiness and sharing their private code directly with trusted champions.',
  },
  {
    title: 'Weighted draw',
    copy:
      'Project Tower runs scheduled draws that weight nominations from the community and select a confirmed classroom.',
  },
];

const guardrails = [
  'Sponsor Slots are created in $800 increments and only matched through Project Tower draws.',
  'Classroom browsing is intentionally disabled. You must use a verified UUID or share code provided by the teacher.',
  'Slots only match to teacher-confirmed classrooms that received community nominations.',
  'Every draw is logged so sponsors and teachers can trace when matches occur.',
];

const communityNotes = [
  'Teachers collect nominations from families, students, and neighbors to boost their odds.',
  'When your slot matches, we introduce you to the classroom with milestones, receipts, and update templates.',
  'No extra paperwork—Project Tower handles compliance, purchasing, and reporting on your behalf.',
];

export default function Sponsor() {
  if (!sponsorsEnabled()) {
    return <Navigate to="/" replace />;
  }

  const { user, role } = useAuth();
  const [resolvedClassroom, setResolvedClassroom] = useState<ResolvedClassroom | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const displayResolvedName = useMemo(() => {
    if (!resolvedClassroom) return null;
    return resolvedClassroom.public_name || 'Confirmed classroom';
  }, [resolvedClassroom]);

  const handleResolved = (classroom: ResolvedClassroom) => {
    setResolvedClassroom(classroom);
    setCheckoutError(null);
  };

  const startCheckout = async () => {
    if (!resolvedClassroom) return;
    setCheckoutLoading(true);
    setCheckoutError(null);

    try {
      const metadata: Record<string, string> = { program: 'classrooms' };
      if (user?.id) {
        metadata.user_id = user.id;
      }
      const inferredName =
        (user?.user_metadata &&
          (user.user_metadata.full_name || user.user_metadata.name || user.user_metadata.display_name)) ||
        null;
      const safeName = inferredName || user?.email?.split('@')[0] || null;
      if (safeName) {
        metadata.sponsor_display_name = safeName;
      }
      if (user?.email) {
        metadata.sponsor_email = user.email;
      }

      const response = await fetch(`${CORE_API}/api/v1/checkout/donate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount_cents: SLOT_VALUE_CENTS,
          destination: 'classrooms',
          classroom_id: resolvedClassroom.id,
          metadata,
        }),
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Unable to create checkout session');
      }

      const raw = await response.json();
      const data = raw?.data ?? raw;
      if (data?.url) {
        if (typeof window !== 'undefined') {
          window.location.assign(data.url);
        }
      } else {
        throw new Error('Missing checkout URL');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to create checkout session';
      setCheckoutError(message);
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="space-y-16">
      <section className="grid items-start gap-8 lg:grid-cols-[1.2fr,0.8fr]">
        <div className="space-y-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-shell px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cocoa/70">
            Sponsor Program
          </span>
          <h1 className="text-4xl font-semibold leading-tight text-cocoa">Fund a Classroom. Change a Year.</h1>
          {sponsorsEnabled() && role === 'sponsor' ? (
            <NavLink
              to="/sponsor/dashboard"
              className="badge bg-brick text-white hover:bg-brickDark"
            >
              Go to your Sponsor Dashboard
            </NavLink>
          ) : null}
          <p className="text-lg text-cocoa/75">
            Paying $800 secures your Sponsor Profile and mints a Sponsor Slot. Project Tower matches your slot—fairly—
            to a teacher-confirmed classroom that the community nominated. Every match adds a new classroom to the tower.
          </p>
          <div className="space-y-4 rounded-3xl bg-white/80 p-4 shadow-soft">
            <DonateShareCodeInput onResolved={handleResolved} />
            <p className="text-xs text-cocoa/60">
              Share codes are private. Ask the teacher you want to support for their Project Tower code or UUID.
            </p>
            {resolvedClassroom ? (
              <div className="rounded-2xl border border-mint/60 bg-mint/10 p-4 text-sm text-cocoa">
                <p className="font-semibold text-cocoa">{displayResolvedName}</p>
                <p className="mt-1 text-cocoa/70">
                  This classroom is {resolvedClassroom.is_confirmed ? 'confirmed' : 'pending confirmation'}. When you continue,
                  we’ll open Stripe to complete your $800 sponsorship and mint the slot.
                </p>
                <button
                  type="button"
                  onClick={startCheckout}
                  disabled={checkoutLoading}
                  className="mt-3 inline-flex items-center justify-center rounded-xl bg-brick px-4 py-2 text-sm font-semibold text-white transition hover:bg-brickDark disabled:opacity-60"
                >
                  {checkoutLoading ? 'Preparing checkout…' : 'Proceed to $800 checkout'}
                </button>
                {checkoutError ? <p className="mt-2 text-xs text-brick">{checkoutError}</p> : null}
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link className="btn" to="/sponsor/signup">
              Join the sponsor list
            </Link>
            <Link className="btn bg-white text-cocoa border border-sand hover:bg-shell" to="/sponsor/recent">
              View recent matches
            </Link>
          </div>
        </div>
        <Card className="space-y-4 bg-gradient-to-br from-brick/10 via-sand to-mint/10 p-6">
          <h2 className="text-xl font-semibold text-cocoa">Match flow</h2>
          <ul className="space-y-3 text-sm text-cocoa/75">
            {howItWorks.map((step) => (
              <li key={step.title}>
                <p className="font-semibold text-cocoa">{step.title}</p>
                <p>{step.copy}</p>
              </li>
            ))}
          </ul>
          <div className="rounded-2xl border border-sand/60 bg-white/80 p-4 text-xs text-cocoa/70">
            <strong className="font-semibold text-cocoa">Reminder:</strong> Paying $800 always equals one slot. No partial
            matches, no browsing classrooms, and every match happens during a scheduled draw.
          </div>
        </Card>
      </section>

      <section className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="text-2xl font-semibold text-cocoa">Designed for safety</h2>
          <span className="text-sm uppercase tracking-wide text-cocoa/60">Sponsor → Slot → Draw → Classroom</span>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {guardrails.map((item) => (
            <Card key={item} className="h-full bg-white/80 p-5 text-sm leading-relaxed text-cocoa/75">
              {item}
            </Card>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[0.9fr,1.1fr]">
        <Card className="space-y-4 bg-white/80 p-6">
          <h2 className="text-xl font-semibold text-cocoa">Community nominations make it fair</h2>
          <p className="text-sm text-cocoa/70">
            Families, students, and neighbors submit nominations. Confirmed classrooms enter weighted draws based on those
            submissions. The higher the nomination count, the better the odds—but every confirmed classroom stays eligible until matched.
          </p>
          <Link to="/sponsor/recent" className="inline-flex items-center text-sm font-semibold text-brick hover:underline">
            See recent draw results →
          </Link>
        </Card>
        <Card className="space-y-4 bg-shell/70 p-6">
          <h3 className="text-lg font-semibold text-cocoa">What to expect after checkout</h3>
          <ul className="space-y-3 text-sm text-cocoa/75">
            {communityNotes.map((note) => (
              <li key={note} className="flex items-start gap-2">
                <span className="mt-1 text-brick">•</span>
                <span>{note}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-cocoa/60">
            Want to nominate someone? Share <Link className="font-semibold text-brick hover:underline" to="/nominate">the nomination page</Link>{' '}
            so their community can help them enter the draw.
          </p>
        </Card>
      </section>
    </div>
  );
}
